import { Observable } from "rxjs";
import { combineEpics } from "redux-observable";

import st from "lib/utils/index";
import { OrdersRepository } from "lib/repositories/index";
import { replaceCompanyRoute } from "modules/app/actions";
import { createResourceInstance } from "modules/utils/index";
import { createFiltersInstance } from "modules/utils/createFilteredItemsInstance";
import createOrdersFiltersInstance from "modules/orders/OrdersListView/createOrdersFiltersInstance";
import { CALL_BULK_ORDERS_ACTION_SUCCESS } from "modules/orders/utils";

export const INITIALIZE_VIEW = "finances/FinancesListView/INITIALIZE_VIEW";
export const DESTROY_VIEW = "finances/FinancesListView/DESTROY_VIEW";
export const LOAD_RECORDS = "finances/FinancesListView/LOAD_RECORDS";
export const FETCH_RECORDS = "finances/FinancesListView/FETCH_RECORDS";
export const FETCH_RECORDS_START = "finances/FinancesListView/FETCH_RECORDS_START";
export const FETCH_RECORDS_SUCCESS = "finances/FinancesListView/FETCH_RECORDS_SUCCESS";
export const FETCH_RECORDS_FAILURE = "finances/FinancesListView/FETCH_RECORDS_FAILURE";
export const CHANGE_FILTERS = "finances/FinancesListView/CHANGE_FILTERS";
export const CHANGE_PAGINATION = "finances/FinancesListView/CHANGE_PAGINATION";
export const SET_FILTERS = "finances/FinancesListView/SET_FILTERS";
export const SET_PAGINATION = "finances/FinancesListView/SET_PAGINATION";
export const SET_SELECTED_ORDERS_IDS = "finances/FinancesListView/SET_SELECTED_ORDERS_IDS";

export const stateSelector = (state) => state.finances.FinancesListView;

const ordersInstance = createResourceInstance({
  LOAD: LOAD_RECORDS,
  FETCH: FETCH_RECORDS,
  FETCH_START: FETCH_RECORDS_START,
  FETCH_SUCCESS: FETCH_RECORDS_SUCCESS,
  FETCH_FAILURE: FETCH_RECORDS_FAILURE,
  stateSelector,
  stateKey: "records",
  getModel: ({ offset, limit, filters }) => {
    return OrdersRepository.search({ offset, limit, ...filters });
  },
});

const filtersInstance = createOrdersFiltersInstance({
  SET_FILTERS,
  getInitialFilters: () => ({
    created_on: { gte: moment().add(-1, "month").startOf("month").format(moment.RUBY_DATE_FORMAT) },
  }),
});

const initialState = {
  records: ordersInstance.initialState,
  ...filtersInstance.initialState,
  selectedOrdersIds: [],
  offset: 0,
  limit: 100,
  hasBulkActions: false,
};

const reducers = {
  ...ordersInstance.reducersMap,
  ...filtersInstance.reducersMap,

  [INITIALIZE_VIEW]: () => ({
    ...initialState,
    hasBulkActions: st.authorize("create_agent_disbursement") || st.authorize("create_platform_disbursement"),
  }),

  [SET_PAGINATION]: (state, { offset = state.offset, limit = state.limit }) => ({
    ...state,
    offset,
    limit,
  }),

  [SET_SELECTED_ORDERS_IDS]: (state, { selectedOrdersIds }) => ({
    ...state,
    selectedOrdersIds,
  }),
};

const viewEpics = [
  ...ordersInstance.epics,
  ...filtersInstance.epics,

  (actions$) => actions$.ofType(CHANGE_FILTERS).map(({ filters }) => setFilters(filters)),

  (actions$) => actions$.ofType(CHANGE_PAGINATION).map(({ offset, limit }) => setPagination({ offset, limit })),

  (actions$) =>
    actions$.ofType(CHANGE_FILTERS).merge(actions$.ofType(CHANGE_PAGINATION)).debounceTime(300).mapTo(fetchRecords()),

  (actions$, { getState }) =>
    actions$
      .ofType(SET_FILTERS)
      .debounceTime(500)
      .map(() => {
        const { filters } = stateSelector(getState());
        const query = filtersInstance._serializeFiltersToLocationQuery(filters);
        return replaceCompanyRoute({ pathname: "/finances", query });
      }),

  (actions$) => actions$.ofType(CHANGE_FILTERS).mapTo(selectOrders([])),

  // A settlement run rewrites the very columns this list is filtered on, and it clears orders
  // it booked nothing for, so the rows go stale the moment it succeeds - with no way to tell
  // from the screen. Left to a manual reload, the same selection is one click away from being
  // settled twice. The agent disbursement has the same staleness but is left alone here.
  (actions$) =>
    actions$
      .ofType(CALL_BULK_ORDERS_ACTION_SUCCESS)
      .filter(({ params: { actionName } }) => actionName === "createPlatformDisbursement")
      .concatMap(() => Observable.of(selectOrders([]), fetchRecords())),
];

const epics = [
  (actions$, store) =>
    actions$
      .ofType(INITIALIZE_VIEW)
      .concatMap(() => combineEpics(...viewEpics)(actions$, store).takeUntil(actions$.ofType(DESTROY_VIEW))),
];

export const { setFilters, setFiltersWithLocationQuery } = filtersInstance.actions;
export const { load: loadRecords, fetch: fetchRecords } = ordersInstance.actions;

export function initializeView() {
  return { type: INITIALIZE_VIEW };
}

export function destroyView() {
  return { type: DESTROY_VIEW };
}

export function setPagination({ offset, limit }) {
  return { type: SET_PAGINATION, offset, limit };
}

export function changePagination({ offset, limit }) {
  return { type: CHANGE_PAGINATION, offset, limit };
}

export function changeFilters(filters) {
  return { type: CHANGE_FILTERS, filters };
}

export function setParamsWithLocationQuery({ o = initialState.offset, l = initialState.limit, ...query } = {}) {
  return (dispatch) => {
    dispatch(setFiltersWithLocationQuery(query));
    dispatch(setPagination({ offset: +o, limit: +l }));
    dispatch(fetchRecords());
  };
}

export function selectOrder(orderId, selected = true) {
  return (dispatch, getState) => {
    let { selectedOrdersIds } = stateSelector(getState());
    if (selected) {
      selectedOrdersIds = selectedOrdersIds.filter((id) => id !== orderId).concat([orderId]);
    } else {
      selectedOrdersIds = selectedOrdersIds.filter((id) => id !== orderId);
    }
    dispatch({ type: SET_SELECTED_ORDERS_IDS, selectedOrdersIds });
  };
}

export function selectOrders(selectedOrdersIds) {
  return { type: SET_SELECTED_ORDERS_IDS, selectedOrdersIds };
}

export function changeFiltersAndResetOffset(filters) {
  return (dispatch) => {
    dispatch(changePagination({ offset: 0 }));
    dispatch(changeFilters(filters));
  };
}

export default st.duck.createDuck(initialState, reducers, epics);



// WEBPACK FOOTER //
// ./src/modules/finances/FinancesListView/index.js